/*
 * Copyright (c) CIRROM 2026.
 */

import { CirromMonitorCRMComparison } from "../../../api/cirrommonitor";
import { toReadableText } from "../../utils/utils";

/*
 * Pure normaliser for the record-review surface.
 *
 * The two review modals keep their change sources apart - suggested changes in
 * one table, Monitor detections in another, behind tabs - which is exactly how
 * two suggestions for the same field stay invisible to the steward deciding
 * between them. This module folds both payloads into one model where the FIELD
 * is the unit of review: a field carries its incumbent CRM value plus every
 * pending candidate, tagged with the source that proposed it.
 *
 * No React, no fetching, no formatting beyond value equivalence - everything
 * here is unit-testable with plain fixtures.
 */

/**
 * "manual" is reserved for the future inline-edit affordance; nothing in v1
 * produces it, but the model carries it so wiring edits later is additive.
 */
export type CandidateSource = "cirrom" | "monitor" | "manual";

export interface FieldCandidate {
  source: CandidateSource;
  /** Proposed value. Empty string when the change carries only a description. */
  value: string;
  /** Cirrom probability (0-100). Monitor detections carry none. */
  confidence?: number;
  /** Human-readable support: a Cirrom description or a LinkedIn profile URL. */
  evidence?: string;
  sourceIds: {
    suggestedChangeId?: number;
    /** MultipleChoice option id - needed by approveMultipleChange later. */
    choiceId?: string;
    monitorId?: number;
  };
  /** Raw payload status, kept for the future action wiring. */
  status?: string;
}

export interface LedgerField {
  /** Canonical backend key, e.g. "first_name". */
  fieldName: string;
  label: string;
  crmValue: string | null;
  /**
   * InterAction stores some company names as free text rather than an account
   * link; the value is still worth showing but is a different fact from a
   * linked account, so the renderer marks it.
   */
  crmValueIsTextCompany?: boolean;
  /** Telephone/address fields owned by the account rather than the contact. */
  fromAccount: boolean;
  /** CRM record id from the explain payload; ad-hoc edits need it. */
  crmId?: string;
  /**
   * No source could vouch for this field, so its blank is "we don't know"
   * rather than "the CRM holds nothing". A blank cell and a missing value are
   * different facts, and that stays true when the fetch is what's missing.
   */
  valueUnknown?: boolean;
  candidates: FieldCandidate[];
  /** Tooltip text supplied by the explain payload, when present. */
  altText?: string;
  /** Which full-record section the field is shown under. */
  group?: FieldGroup;
}

/*
 * The sections of the full record, in display order. Each roster entry names
 * its section so the roster is the single place a field is defined - adding a
 * field here is the whole job of adding it to the review surface.
 */
export const FIELD_GROUP_ORDER = ["Name", "Employment", "Address"] as const;
export type FieldGroup = typeof FIELD_GROUP_ORDER[number];

export interface ReviewIdentity {
  firstName?: string;
  lastName?: string;
  company?: string;
  jobTitle?: string;
  contactSource?: string;
}

export interface ReviewLinks {
  crmContact?: string;
  crmAccount?: string;
  companyWebsite?: string;
  /** A concrete LinkedIn profile from Monitor beats a name search. */
  linkedInProfile?: string;
}

export type EmailStatusSeverity = "success" | "warning" | "error";

export interface RecordReviewModel {
  entityType: "CONTACT" | "ACCOUNT";
  /**
   * Whether the explain payload loaded. Ad-hoc edits ride the
   * suggested-changes pipeline, so without it there is nothing to write
   * through and the pencils stay hidden.
   */
  hasExplainData: boolean;
  identity: ReviewIdentity;
  /**
   * The CRM's primary email is a personal address. An email derived from a
   * company account is a WORK address, so it only belongs on the primary field
   * when that field isn't already holding a personal one - Monitor is the only
   * source that knows which it is.
   */
  primaryEmailIsPersonal: boolean;
  emailStatuses: [EmailStatusSeverity, string][];
  duplicateGroupId: number | null;
  sourceFolderName?: string;
  links: ReviewLinks;
  /** Fields with at least one pending candidate, sorted contested-first. */
  ledgerFields: LedgerField[];
  /** Fields with nothing pending, in canonical display order. */
  fullRecordFields: LedgerField[];
  /** The record's own CRM ids - what an ad-hoc write is keyed on. */
  crmContactId?: string | null;
  crmAccountId?: string | null;
  /**
   * The batch whose read of the CRM the values above come from. A write made
   * since then is in the CRM but not yet in these values.
   */
  snapshotBatchId?: number | null;
}

export interface ExplainChange {
  cirrom_value: string | null;
  probability: number;
  suggested_change_id: number;
  status: string;
  description: any;
  link_change_type: any;
}

export interface ExplainFieldEntry {
  field_name: string;
  crm_value: string | null;
  cirrom_changes: ExplainChange[];
  crm_id?: string;
  alt_text?: string;
}

export interface ExplainEmailData {
  last_email_delivered?: string;
  last_email_open?: string;
  last_email_hard_bounce?: string;
  last_email_soft_bounce?: string;
}

export interface ExplainResponse {
  contact_data: ExplainFieldEntry[];
  email_data?: ExplainEmailData;
  source_folder_name?: string;
  company_website?: string;
  duplicate_group_id?: number | null;
  integrated_system_id?: number;
  record_id?: number;
  crm_contact_id?: string | null;
  crm_account_id?: string | null;
  crm_urls?: { contact?: string | null; account?: string | null } | null;
  /* The backend's ChangeEntity name; absent on some legacy payloads. */
  entity_type?: "CONTACT" | "ACCOUNT";
  /** The batch whose read of the CRM these values come from. */
  batch_id?: number | null;
}

interface FieldSpec {
  key: string;
  label: string;
  group: FieldGroup;
  /** Which owner id decides the from-account marker. */
  owner?: "phone" | "address";
  /** Only meaningful when Monitor data exists (the CRM has no such column). */
  monitorOnly?: boolean;
  /**
   * Not every CRM has this column, and the backend only sends the key for the
   * ones that do. When neither payload mentions it the field is left out
   * altogether rather than shown as empty.
   */
  omitWhenAbsent?: boolean;
}

/*
 * Order and labels lifted from RecordDetails.getRecordRows so the two surfaces
 * never disagree about what a contact looks like. Within a section, fields
 * appear in roster order.
 */
const ACCOUNT_LEVEL_FIELDS: FieldSpec[] = [
  { key: "telephone", label: "Telephone", group: "Employment", owner: "phone" },
  { key: "street", label: "Street", group: "Address", owner: "address" },
  { key: "city", label: "City", group: "Address", owner: "address" },
  {
    key: "stateorprovince",
    label: "State",
    group: "Address",
    owner: "address",
  },
  { key: "zipcode", label: "Zip Code", group: "Address", owner: "address" },
  { key: "country", label: "Country", group: "Address", owner: "address" },
];

const CONTACT_FIELDS: FieldSpec[] = [
  { key: "salutation", label: "Salutation", group: "Name" },
  { key: "first_name", label: "First Name", group: "Name" },
  { key: "middle_name", label: "Middle Name", group: "Name" },
  { key: "last_name", label: "Last Name", group: "Name" },
  { key: "goes_by", label: "Goes By", group: "Name" },
  { key: "suffix", label: "Suffix", group: "Name" },
  { key: "company", label: "Company", group: "Employment" },
  { key: "job_title", label: "Job Title", group: "Employment" },
  { key: "primary_email", label: "Email", group: "Employment" },
  {
    key: "secondary_email",
    label: "Secondary Email",
    group: "Employment",
    monitorOnly: true,
  },
  {
    key: "linkedin_url",
    label: "LinkedIn URL",
    group: "Employment",
    omitWhenAbsent: true,
  },
  { key: "address_linkage", label: "Address Linkage", group: "Address" },
  ...ACCOUNT_LEVEL_FIELDS,
];

/* Exported so account surfaces synthesize payloads from the same roster. */
export const ACCOUNT_FIELDS: FieldSpec[] = [
  { key: "company", label: "Company", group: "Employment" },
  ...ACCOUNT_LEVEL_FIELDS,
];

/*
 * The steward-facing label for an internal field name, from the same roster
 * the ledger uses so a field reads the same in the applied history as it does
 * in the pending list. Fields outside the roster fall back to readable text.
 */
export const fieldLabel = (fieldName: string): string =>
  CONTACT_FIELDS.find((spec) => spec.key === fieldName)?.label ??
  toReadableText(fieldName);

/**
 * Two values are the same fact when they differ only by case or whitespace.
 * Used both to drop Monitor echoes of the CRM value and to detect agreement
 * between sources.
 */
export const normalizeValue = (value: string | null | undefined): string =>
  (value ?? "").trim().replace(/\s+/g, " ").toLowerCase();

export interface CandidateValueGroup {
  /** Display value - the first raw spelling seen for this group. */
  value: string;
  candidates: FieldCandidate[];
  sources: CandidateSource[];
}

/**
 * Distinct proposed values for a field, each with the sources standing behind
 * it. One group backed by two sources is agreement; several groups is a
 * contest the steward has to resolve.
 */
export const groupCandidates = (
  candidates: FieldCandidate[]
): CandidateValueGroup[] => {
  const groups: CandidateValueGroup[] = [];
  const byValue = new Map<string, CandidateValueGroup>();
  candidates.forEach((candidate) => {
    const key = normalizeValue(candidate.value);
    let group = byValue.get(key);
    if (!group) {
      group = { value: candidate.value, candidates: [], sources: [] };
      byValue.set(key, group);
      groups.push(group);
    }
    group.candidates.push(candidate);
    if (!group.sources.includes(candidate.source)) {
      group.sources.push(candidate.source);
    }
  });
  return groups;
};

/**
 * Value groups collapsed into units that can be rejected independently.
 *
 * Rejection acts on a whole suggested change, not on one of its values: a
 * MultipleChoice change renders as one row per option but IS a single change,
 * so rejecting any option rejects the lot. Two value groups sharing a change
 * id are therefore one rejectable thing, and a bulk reject that treats them as
 * two posts the same id twice and reports the duplicate refusal as a change
 * that failed to reject.
 *
 * Grouping for DISPLAY stays per value - the steward still sees each option.
 */
export const rejectUnits = (
  candidates: FieldCandidate[]
): CandidateValueGroup[] => {
  const units: CandidateValueGroup[] = [];
  const unitByChangeId = new Map<number, CandidateValueGroup>();
  groupCandidates(candidates).forEach((group) => {
    const changeIds = Array.from(
      new Set(
        group.candidates
          .map((candidate) => candidate.sourceIds.suggestedChangeId)
          .filter((id): id is number => id !== undefined)
      )
    );
    const existing = changeIds
      .map((id) => unitByChangeId.get(id))
      .find(Boolean);
    const unit = existing ?? group;
    if (existing) {
      existing.candidates.push(...group.candidates);
      group.sources.forEach((source) => {
        if (!existing.sources.includes(source)) {
          existing.sources.push(source);
        }
      });
    } else {
      units.push(group);
    }
    changeIds.forEach((id) => unitByChangeId.set(id, unit));
  });
  return units;
};

export type LedgerRowVariant =
  | "single"
  | "contested"
  | "agreement"
  | "multipleChoice";

export const getFieldVariant = (field: LedgerField): LedgerRowVariant => {
  const groups = groupCandidates(field.candidates);
  const sourceCount = new Set(field.candidates.map((c) => c.source)).size;
  if (groups.length > 1) {
    return sourceCount > 1 ? "contested" : "multipleChoice";
  }
  return sourceCount > 1 ? "agreement" : "single";
};

export interface LedgerSummary {
  total: number;
  /** Fields needing a choice between values (contested or multiple-choice). */
  contested: number;
  /** Changes that fill an empty CRM field rather than correcting a value. */
  fills: number;
}

/**
 * The triage story of the ledger in three numbers: how much is pending, how
 * much needs a human choice, and how much is low-risk fill of empty fields.
 */
export const summarizeLedger = (fields: LedgerField[]): LedgerSummary => ({
  total: fields.length,
  contested: fields.filter((field) => {
    const variant = getFieldVariant(field);
    return variant === "contested" || variant === "multipleChoice";
  }).length,
  fills: fields.filter((field) => !normalizeValue(field.crmValue)).length,
});

/*
 * Sort bands for the ledger: anything that needs the steward to CHOOSE between
 * values comes first, then corroborated changes, then plain single ones.
 */
const VARIANT_BAND: Record<LedgerRowVariant, number> = {
  contested: 0,
  multipleChoice: 1,
  agreement: 2,
  single: 3,
};

/** Statuses that still await a decision; everything else is history. */
const PENDING_STATUSES = new Set(["Active", "MultipleChoice"]);

const getDescriptionText = (description: any): string | undefined => {
  if (!description) return undefined;
  if (typeof description === "string") {
    try {
      return JSON.parse(description).Description;
    } catch {
      return description;
    }
  }
  return description.Description;
};

export const cirromCandidatesForEntry = (
  entry: ExplainFieldEntry
): FieldCandidate[] =>
  (entry.cirrom_changes ?? [])
    .filter((change) => PENDING_STATUSES.has(change.status))
    .flatMap((change) => {
      const evidence = getDescriptionText(change.description);
      if (change.status === "MultipleChoice" && change.cirrom_value) {
        /*
         * A MultipleChoice change packs its options into cirrom_value as a
         * JSON array; each option is its own candidate so the contest between
         * them is visible in the same grammar as a cross-source contest.
         */
        try {
          const options: { id: string; new_value: string }[] = JSON.parse(
            change.cirrom_value
          );
          return options.map((option) => ({
            source: "cirrom" as const,
            value: option.new_value,
            confidence: change.probability,
            evidence,
            sourceIds: {
              suggestedChangeId: change.suggested_change_id,
              choiceId: option.id,
            },
            status: change.status,
          }));
        } catch {
          // Malformed options fall through to a plain candidate below.
        }
      }
      return [
        {
          source: "cirrom" as const,
          value: change.cirrom_value ?? "",
          confidence: change.probability,
          evidence,
          sourceIds: { suggestedChangeId: change.suggested_change_id },
          status: change.status,
        },
      ];
    });

/**
 * The value a change can lend the record's IDENTITY when the CRM holds none.
 *
 * Two things disqualify a change from speaking for the record. A MultipleChoice
 * change packs its options into cirrom_value as a JSON array - that string is
 * not a name, and lending it verbatim put raw JSON in the card heading, the
 * page title and the shared email. And a change that is no longer pending is
 * history: a rejected proposal must not name the record it was rejected for.
 */
const identityValueFromChange = (change: ExplainChange): string | undefined => {
  if (!PENDING_STATUSES.has(change.status)) return undefined;
  if (change.status === "MultipleChoice") return undefined;
  return change.cirrom_value ?? undefined;
};

const monitorCandidatesForField = (
  monitor: CirromMonitorCRMComparison,
  fieldKey: string,
  crmValue: string | null
): FieldCandidate[] => {
  /*
   * A field the steward already decided through Monitor is history, not a
   * pending candidate - accepted_results is the record of those decisions.
   */
  const alreadyDecided = monitor.accepted_results?.some(
    (accepted) => accepted.field === fieldKey
  );
  if (alreadyDecided) return [];

  const linkedInUrl = monitor.pending_results?.[0]?.linkedin_url;
  return (monitor.monitor_results?.[fieldKey] ?? [])
    .filter(
      (detection) =>
        detection.value &&
        normalizeValue(detection.value) !== normalizeValue(crmValue)
    )
    .map((detection) => ({
      source: "monitor" as const,
      value: detection.value,
      evidence: linkedInUrl,
      sourceIds: { monitorId: detection.id },
    }));
};

/** The most recent of the dates given, skipping blanks and unparseable ones. */
const latestDate = (
  ...values: (string | null | undefined)[]
): string | undefined =>
  values
    .filter((value): value is string => Boolean(value))
    .filter((value) => !Number.isNaN(Date.parse(value)))
    .sort((a, b) => Date.parse(b) - Date.parse(a))[0];

const onDate = (value: string) => new Date(Date.parse(value)).toDateString();

/*
 * Deliverability, read from both payloads at once rather than whichever
 * arrived. Explain's email lookup can come back empty for a contact Monitor
 * has history on - it matches on the email table, Monitor reads the CRM's own
 * activity - and preferring it wholesale told a steward a hard-bounced address
 * had "never been emailed". So activity takes explain's richer opened /
 * delivered fact and falls back to Monitor's, and each bounce takes the latest
 * date either source knows: a bounce anyone reports is never hidden.
 */
const emailStatusesFrom = (
  emailData: ExplainEmailData | undefined,
  crmResults: Record<string, any> | undefined
): [EmailStatusSeverity, string][] => {
  const statuses: [EmailStatusSeverity, string][] = [];
  if (emailData?.last_email_open) {
    statuses.push([
      "success",
      `Last opened email on ${onDate(emailData.last_email_open)}.`,
    ]);
  } else if (emailData?.last_email_delivered) {
    statuses.push([
      "success",
      `Last delivered email on ${onDate(emailData.last_email_delivered)}.`,
    ]);
  } else if (crmResults?.last_email_activity) {
    statuses.push([
      "success",
      `Last email activity on ${onDate(crmResults.last_email_activity)}.`,
    ]);
  }

  const hardBounce = latestDate(
    emailData?.last_email_hard_bounce,
    crmResults?.last_email_hard_bounce
  );
  if (hardBounce) {
    statuses.push([
      "error",
      `Email hard bounce event on ${onDate(hardBounce)}.`,
    ]);
  }
  const softBounce = latestDate(
    emailData?.last_email_soft_bounce,
    crmResults?.last_email_soft_bounce
  );
  if (softBounce) {
    statuses.push([
      "error",
      `Email soft bounce event on ${onDate(softBounce)}.`,
    ]);
  }
  return statuses;
};

export const normalizeRecordReview = (
  explain: ExplainResponse | null,
  monitor: CirromMonitorCRMComparison | null,
  crmUrls: { contact?: string; account?: string } | null
): RecordReviewModel => {
  const explainByField = new Map<string, ExplainFieldEntry>();
  (explain?.contact_data ?? []).forEach((entry) => {
    explainByField.set(entry.field_name, entry);
  });

  /* CONTACT covers the null-explain (monitor-only) and legacy paths. */
  const entityType: "CONTACT" | "ACCOUNT" = explain?.entity_type ?? "CONTACT";
  const specs = entityType === "ACCOUNT" ? ACCOUNT_FIELDS : CONTACT_FIELDS;

  /*
   * From-account ownership: an address or phone whose owner id matches the
   * contact's account id belongs to the account record, and editing it edits
   * every contact at that account. The explain payload carries the ids as
   * field entries; Monitor carries them flat on crm_results.
   */
  const ownerValue = (key: string): string | null =>
    explainByField.get(key)?.crm_value ??
    ((monitor?.crm_results as any)?.[key] || null);
  const accountId = ownerValue("crm_account_id");
  const isFromAccount = (owner?: "phone" | "address"): boolean => {
    if (!owner || !accountId) return false;
    const ownerId = ownerValue(
      owner === "phone" ? "phone_owner_id" : "address_owner_id"
    );
    return Boolean(ownerId) && ownerId === accountId;
  };

  const fields: LedgerField[] = [];
  specs.forEach((spec) => {
    if (spec.monitorOnly && !monitor) return;

    const entry = explainByField.get(spec.key);
    const monitorKnowsField =
      Boolean(monitor) &&
      Object.prototype.hasOwnProperty.call(monitor.crm_results ?? {}, spec.key);
    if (spec.omitWhenAbsent && !entry && !monitorKnowsField) return;

    let crmValue = entry?.crm_value ?? null;
    let crmValueIsTextCompany = false;
    if (spec.key === "company" && !crmValue) {
      const textCompany = explainByField.get("text_company")?.crm_value;
      if (textCompany) {
        crmValue = textCompany;
        crmValueIsTextCompany = true;
      }
    }
    if (crmValue === null && monitor) {
      crmValue = (monitor.crm_results as any)?.[spec.key] ?? null;
    }

    const candidates = [
      ...(entry ? cirromCandidatesForEntry(entry) : []),
      ...(monitor && entityType === "CONTACT"
        ? monitorCandidatesForField(monitor, spec.key, crmValue)
        : []),
    ];

    /*
     * "Unknown" is only true when the explain payload never arrived. If it
     * did and simply carries no entry for this field, the CRM holds nothing
     * there - which is "empty", a different fact. Gating on the payload
     * rather than on the entry keeps a loaded record from claiming ignorance
     * about every field the response happens not to mention.
     */
    const nothingCanVouch = !explain && !monitorKnowsField;
    fields.push({
      fieldName: spec.key,
      label: spec.label,
      crmValue,
      crmValueIsTextCompany: crmValueIsTextCompany || undefined,
      fromAccount: isFromAccount(spec.owner),
      crmId: entry?.crm_id,
      valueUnknown: nothingCanVouch ? true : undefined,
      candidates,
      altText: entry?.alt_text,
      group: spec.group,
    });
  });

  const ledgerFields = fields
    .filter((field) => field.candidates.length > 0)
    .sort(
      (a, b) =>
        VARIANT_BAND[getFieldVariant(a)] - VARIANT_BAND[getFieldVariant(b)]
    );
  const fullRecordFields = fields.filter(
    (field) => field.candidates.length === 0
  );

  let emailStatuses: [EmailStatusSeverity, string][] = [];
  if (entityType === "CONTACT") {
    emailStatuses = emailStatusesFrom(
      explain?.email_data,
      monitor?.crm_results
    );
    // No activity at all is itself a fact worth a banner - both modals say so.
    if ((explain || monitor) && emailStatuses.length === 0) {
      emailStatuses.push(["warning", "This contact has never been emailed."]);
    }
  }

  const identityField = (key: string): string | undefined => {
    const entry = explainByField.get(key);
    if (entry?.crm_value) return entry.crm_value;
    const proposed = (entry?.cirrom_changes ?? [])
      .map(identityValueFromChange)
      .find(Boolean);
    if (proposed) return proposed;
    return (monitor?.crm_results as any)?.[key] || undefined;
  };

  return {
    entityType,
    hasExplainData: explain !== null && explain !== undefined,
    identity: {
      firstName: identityField("first_name"),
      lastName: identityField("last_name"),
      company: identityField("company") ?? identityField("text_company"),
      jobTitle: identityField("job_title"),
      contactSource: identityField("contact_source"),
    },
    primaryEmailIsPersonal: Boolean(
      monitor?.crm_results?.primary_email_is_personal
    ),
    emailStatuses,
    duplicateGroupId: explain?.duplicate_group_id ?? null,
    sourceFolderName: explain?.source_folder_name,
    links: {
      // The record's own links first; the change-keyed lookup is the fallback.
      crmContact: explain?.crm_urls?.contact || crmUrls?.contact,
      crmAccount: explain?.crm_urls?.account || crmUrls?.account,
      companyWebsite: explain?.company_website,
      linkedInProfile: monitor?.pending_results?.[0]?.linkedin_url,
    },
    ledgerFields,
    fullRecordFields,
    crmContactId:
      explain?.crm_contact_id ?? monitor?.crm_results?.crm_contact_id ?? null,
    crmAccountId:
      explain?.crm_account_id ?? ownerValue("crm_account_id") ?? null,
    snapshotBatchId: explain?.batch_id ?? null,
  };
};
